import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, isDevMode } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { API_CONFIG, INCIDENT_FORM_FIELD_PROVIDER, type ApiConfig, type IncidentFormFieldProvider } from '@shared';
import { INCIDENTS_AUTH_GUARD } from '@incident-management';
import { of } from 'rxjs';
import { mcTripsStaffGuard } from './auth/mc-trips-staff.guard';
import { providePrimeNG } from 'primeng/config';
import { Theme } from 'src/theme';
import { GoogleLoginProvider, type SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { CookieModule, CookieService } from 'ngx-cookie';
import { JwtInterceptor } from 'src/app/auth/token.interceptor';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';

const googleSignInClientId = (environment.config as Record<string, string>).GOOGLE_SIGN_IN_CLIENT_ID ?? '';

const apiConfig: ApiConfig = {
  dataApiUrl: environment.config.DATA_API_URL,
  staffPhotoUrl: environment.config.STAFF_PHOTO_URL,
  mcapiKey: environment.config.MCAPI_KEY,
  mcHost: environment.config.MC_HOST,
  pelicanAccountUrl: environment.config.PELICAN_ACCOUNT_URL,
  pelicanStoryblokToken: 'zeg4p6oRv5wWYqChiQgX3gtt',
  pelicanGuideYears: [2023, 2024, 2025],
  // Optional: Drive API browser key (restrict in Google Cloud to Drive API + this app’s HTTP referrers).
  // When set, Resources → USA/Canada Location Packets lists files from the shared folder.
  googleDriveBrowserApiKey: undefined,
};

// Minimal form field provider so incident module loads (returns empty form fields; full forms need main app provider).
const mcTripsFormFieldProvider: IncidentFormFieldProvider = {
  getIncidentReportFields: (_type, _dateCreated) => of([]),
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideNoopAnimations(),
    { provide: API_CONFIG, useValue: apiConfig },
    { provide: INCIDENTS_AUTH_GUARD, useValue: mcTripsStaffGuard },
    { provide: INCIDENT_FORM_FIELD_PROVIDER, useValue: mcTripsFormFieldProvider },
    importProvidersFrom(CookieModule.withOptions()),
    {
      // Minimal SocialAuthService configuration so LocalAuthService / Trip Journals can inject it.
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(googleSignInClientId, {
              oneTapEnabled: false,
              scopes: ['email', 'profile'],
            }),
          },
        ],
        onError: (err: unknown) => {
          // eslint-disable-next-line no-console
          console.error(err);
        },
      } as SocialAuthServiceConfig,
    },
    CookieService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    MessageService,
    providePrimeNG({
      theme: {
        preset: Theme.McTheme,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
            order: 'base, primeng, primeng-custom, utilities, components, theme',
          },
        },
      },
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
